import React, { useRef, useState, useEffect } from "react";
import { TextField } from "@mui/material";
import "./CoordinatesInput.scss";

const CoordinatesInput = ({
  value,
  handleValueChange,
  placeholder = "37.7749",
  label = "Latitude",
  maxAbs = 90, // 90 for Lat, 180 for Lng
  maxLength = 7, // keep generous for sign + dot + digits
  autoDecimalAt = 2, // 2 for Lat, 3 for Lng
  ...props
}) => {
  const inputRef = useRef(null);
  const [displayValue, setDisplayValue] = useState(value || "");

  useEffect(() => {
    setDisplayValue(value || "");
  }, [value]);

  const isValidNumberDraft = (s) => {
    // allow "", "-" and numbers with optional single leading "-" and single "."
    if (s === "" || s === "-") return true;
    if (!/^-?\d*\.?\d*$/.test(s)) return false;

    // not more than one minus or not at start
    if ((s.match(/-/g) || []).length > 1) return false;
    if (s.includes("-") && s.indexOf("-") !== 0) return false;

    // range check (ignore trailing dot)
    const asNum = parseFloat(s.endsWith(".") ? s.slice(0, -1) : s);
    if (Number.isNaN(asNum)) return true;
    return Math.abs(asNum) <= maxAbs;
  };

  const shouldAutoAddDecimal = (inputValue) => {
    const digitsOnly = inputValue.replace("-", "").replace(".", "");
    return (
      /^\d+$/.test(digitsOnly) &&
      !inputValue.includes(".") &&
      digitsOnly.length === autoDecimalAt
    );
  };

  const commitValue = (next) => {
    setDisplayValue(next);
    handleValueChange(next);
  };

  const handleInputChange = (e) => {
    let next = e.target.value;

    if (shouldAutoAddDecimal(next)) {
      next += ".";
      commitValue(next);

      // move caret after the auto-inserted dot
      requestAnimationFrame(() => {
        const el = inputRef.current;
        if (el && typeof el.setSelectionRange === "function") {
          const pos = next.length;
          try {
            el.setSelectionRange(pos, pos);
          } catch {}
        } else {
          inputRef.current?.focus?.();
        }
      });
      return;
    }

    commitValue(next);
  };

  // HARD BLOCK non-numeric keys (still allow nav/edit keys)
  const handleKeyDown = (e) => {
    const navKeys = new Set([
      "Backspace",
      "Delete",
      "ArrowLeft",
      "ArrowRight",
      "ArrowUp",
      "ArrowDown",
      "Home",
      "End",
      "Tab",
      "Enter",
      "Escape",
    ]);

    if (navKeys.has(e.key)) return;

    const isDigit = /^[0-9]$/.test(e.key);
    const isMinus = e.key === "-";
    const isDot = e.key === ".";

    const el = inputRef.current;
    const selStart = el?.selectionStart ?? 0;
    const selEnd = el?.selectionEnd ?? selStart;
    const current = displayValue ?? "";

    // candidate string after this keystroke
    const candidate =
      current.slice(0, selStart) + e.key + current.slice(selEnd);

    // allow: digits, one leading -, one dot, within range
    if (isDigit || isMinus || isDot) {
      if (!isValidNumberDraft(candidate)) {
        e.preventDefault();
      }
      return;
    }

    // Block everything else
    e.preventDefault();
  };

  // Block invalid typed characters even on some mobile keyboards
  const handleBeforeInput = (e) => {
    if (typeof e.data !== "string") return;
    const el = inputRef.current;
    const selStart = el?.selectionStart ?? 0;
    const selEnd = el?.selectionEnd ?? selStart;
    const candidate =
      (displayValue ?? "").slice(0, selStart) +
      e.data +
      (displayValue ?? "").slice(selEnd);

    if (!isValidNumberDraft(candidate)) {
      e.preventDefault();
    }
  };

  // Sanitize pasted content
  // const handlePaste = (e) => {
  //   const text = (e.clipboardData || window.clipboardData).getData("text");
  //   const sanitized = text.replace(/[^\d\-.]/g, ""); // keep digits, dot, minus
  //   const el = inputRef.current;
  //   const selStart = el?.selectionStart ?? 0;
  //   const selEnd = el?.selectionEnd ?? selStart;
  //   const candidate =
  //     (displayValue ?? "").slice(0, selStart) +
  //     sanitized +
  //     (displayValue ?? "").slice(selEnd);

  //   if (!isValidNumberDraft(candidate)) {
  //     e.preventDefault();
  //     return;
  //   }
  // };

  const getPattern = () => {
    const maxDigits = String(maxAbs).length; // rough hint for browser validation
    return `^-?\\d{1,${maxDigits}}(\\.\\d{0,6})?$`;
  };

  return (
    <div className="coordinates-input">
      <TextField
        value={displayValue}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onBeforeInput={handleBeforeInput}
        placeholder={placeholder}
        label={label}
        variant="outlined"
        size="medium"
        autoComplete="off"
        type="text"
        inputRef={inputRef} // <-- important: actual input element
        inputProps={{
          inputMode: "decimal", // numeric keyboard on mobile
          maxLength,
          pattern: getPattern(),
        }}
        sx={{ flex: 1 }}
        {...props}
      />
    </div>
  );
};

export default CoordinatesInput;
